import React, { useState, useRef, useEffect } from 'react';
import ProgressSteps from './ProgressSteps';
import StructuredResponse from './StructuredResponse';
import ContextIndicator from './ContextIndicator';
import FollowUpSuggestions from './FollowUpSuggestions';
import ComparisonHelper from './ComparisonHelper';

const MAX_CONTEXT_MESSAGES = 5; // Keep last 5 messages in context

// Helper: Format duration from seconds to HH:MM:SS or MM:SS
const formatDuration = (seconds) => {
  if (!seconds || isNaN(seconds)) return '0:00';

  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  const secs = Math.floor(seconds % 60);

  if (hours > 0) {
    return `${hours}:${minutes.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  } else {
    return `${minutes}:${secs.toString().padStart(2, '0')}`;
  }
};

// Helper: Check if metric is a duration metric (should be formatted as time)
const isDurationMetric = (metricName) => {
  const durationMetrics = [
    'averagesessionduration',
    'userengagementduration',
    'sessionduration'
  ];
  return durationMetrics.some(dm => metricName.toLowerCase().includes(dm));
};

export default function ChatInterface({ propertyId, onSendQuery, suggestedQuery, onQueryProcessed }) {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [progressStep, setProgressStep] = useState(0);
  const [cancelSignal, setCancelSignal] = useState(null);
  const [contextMessages, setContextMessages] = useState([]);
  const messagesEndRef = useRef(null);

  // Handle suggested query from sidebar
  useEffect(() => {
    if (suggestedQuery) {
      setInput(suggestedQuery);
      if (onQueryProcessed) {
        onQueryProcessed();
      }
      // Focus input
      setTimeout(() => {
        document.querySelector('input[type="text"]')?.focus();
      }, 100);
    }
  }, [suggestedQuery, onQueryProcessed]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  // Clear messages and context when property changes
  useEffect(() => {
    setMessages([]);
    setContextMessages([]);
  }, [propertyId]);

  // Update context when messages change
  useEffect(() => {
    // Keep only last MAX_CONTEXT_MESSAGES user+assistant pairs
    const relevantMessages = messages.filter(
      m => m.type === 'user' || m.type === 'assistant'
    );
    const recentContext = relevantMessages.slice(-MAX_CONTEXT_MESSAGES * 2);
    setContextMessages(recentContext);
  }, [messages]);

  const handleClearContext = () => {
    setMessages([]);
    setContextMessages([]);
  };

  const handleSubmit = async (e, retryQuery = null) => {
    e?.preventDefault();
    if ((!input.trim() && !retryQuery) || loading) return;

    const userMessage = retryQuery || input.trim();
    if (!retryQuery) {
      setInput('');
    }

    if (!retryQuery) {
      setMessages((prev) => [...prev, { type: 'user', content: userMessage }]);
    }

    // Setup cancellation
    const controller = new AbortController();
    setCancelSignal(controller);
    setLoading(true);
    setProgressStep(1);

    // Simulate progress steps
    const progressTimer1 = setTimeout(() => setProgressStep(2), 1000);
    const progressTimer2 = setTimeout(() => setProgressStep(3), 2500);

    try {
      // Prepare context for API (last 3 message pairs)
      const contextForAPI = contextMessages
        .slice(-6) // Last 3 pairs (user + assistant)
        .map(msg => ({
          role: msg.type === 'user' ? 'user' : 'assistant',
          content: msg.content
        }));

      const result = await onSendQuery(userMessage, propertyId, contextForAPI);

      // Clear timers
      clearTimeout(progressTimer1);
      clearTimeout(progressTimer2);
      setProgressStep(4); // Hotovo!

      // Brief pause to show "Hotovo!"
      await new Promise(resolve => setTimeout(resolve, 500));

      // Handle error response from backend
      if (result.success === false && result.error) {
        setMessages((prev) => [
          ...prev,
          {
            type: 'error',
            error: result.error,
            data: result.data, // May have data even with error
            originalQuery: userMessage
          }
        ]);
      } else {
        // Successful response
        setMessages((prev) => [
          ...prev,
          {
            type: 'assistant',
            content: result.response,
            data: result.data,
            fromCache: result.fromCache,
            aiLimited: result.aiLimited // Flag if AI had issues but data is available
          }
        ]);
      }
    } catch (error) {
      // Clear timers on error
      clearTimeout(progressTimer1);
      clearTimeout(progressTimer2);

      // Check if cancelled
      if (controller.signal.aborted) {
        setMessages((prev) => [
          ...prev,
          {
            type: 'system',
            content: 'Dotaz byl zrušen.'
          }
        ]);
      } else {
        // Network or unexpected error
        setMessages((prev) => [
          ...prev,
          {
            type: 'error',
            error: {
              title: 'Chyba při komunikaci',
              message: error.message || 'Nepodařilo se spojit se serverem.',
              suggestion: 'Zkontrolujte připojení a zkuste znovu.',
              retryable: true
            },
            originalQuery: userMessage
          }
        ]);
      }
    } finally {
      setLoading(false);
      setProgressStep(0);
      setCancelSignal(null);
    }
  };

  const handleCancel = () => {
    if (cancelSignal) {
      cancelSignal.abort();
    }
  };

  const handleRetry = (query) => {
    handleSubmit(null, query);
  };

  const handleSuggestedQuery = (query) => {
    setInput(query);
  };

  return (
    <div className="flex-1 flex flex-col overflow-hidden">
      {/* Context Indicator */}
      {contextMessages.length > 0 && (
        <ContextIndicator
          contextMessages={contextMessages}
          onClear={handleClearContext}
        />
      )}

      {/* Chat Messages */}
      <div className="flex-1 overflow-y-auto space-y-6 pb-6">
        {messages.map((message, idx) => (
          <div key={idx} className="flex items-start gap-4">
            {/* Avatar */}
            <div className={`flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center ${
              message.type === 'user'
                ? 'bg-muted-light/20 dark:bg-muted-dark/20'
                : message.type === 'assistant'
                  ? 'bg-primary-light dark:bg-primary-dark'
                  : 'bg-gray-200 dark:bg-gray-700'
            }`}>
              <span className={`material-symbols-outlined text-base ${
                message.type === 'user'
                  ? 'text-muted-light dark:text-muted-dark'
                  : message.type === 'assistant'
                    ? 'text-primary'
                    : 'text-gray-600 dark:text-gray-400'
              }`}>
                {message.type === 'user' ? 'person' : message.type === 'assistant' ? 'auto_awesome' : 'info'}
              </span>
            </div>

            {/* Message Content */}
            <div className="flex-1 min-w-0">
              <p className="font-semibold text-text-light dark:text-text-dark mb-1">
                {message.type === 'user' ? 'Váš dotaz:' : message.type === 'assistant' ? 'AI Assistant' : 'Systém'}
              </p>

              {/* User Message */}
              {message.type === 'user' && (
                <div className="bg-primary-light dark:bg-primary-dark p-4 rounded-lg rounded-tl-none">
                  <p className="text-primary dark:text-blue-200">{message.content}</p>
                </div>
              )}

              {/* Assistant Message */}
              {message.type === 'assistant' && (
                <>
                  {message.aiLimited && (
                    <div className="mb-2 p-3 bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg text-yellow-700 dark:text-yellow-300 text-sm flex items-center gap-2">
                      <span className="material-symbols-outlined text-lg">warning</span>
                      AI analýza dočasně nedostupná - zobrazuji surová data
                    </div>
                  )}

                  <StructuredResponse
                    content={message.content}
                    fromCache={message.fromCache}
                  />

                  {/* Data Table */}
                  {message.data?.rows?.length > 0 && (
                    <details className="mt-4 border border-border-light dark:border-border-dark rounded-lg overflow-hidden">
                      <summary className="bg-background-light dark:bg-background-dark px-4 py-3 cursor-pointer font-semibold text-primary hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors flex items-center gap-2">
                        <span className="material-symbols-outlined text-lg">table_chart</span>
                        Zobrazit detailní data ({message.data.rows.length} záznamů)
                      </summary>
                      <div className="overflow-x-auto">
                        <table className="w-full">
                          <thead className="bg-primary text-white">
                            <tr>
                              {message.data.dimensions.map((dim) => (
                                <th key={dim} className="px-4 py-3 text-left text-sm font-semibold">{dim}</th>
                              ))}
                              {message.data.metrics.map((metric) => (
                                <th key={metric} className="px-4 py-3 text-left text-sm font-semibold">{metric}</th>
                              ))}
                            </tr>
                          </thead>
                          <tbody className="bg-surface-light dark:bg-surface-dark">
                            {message.data.rows.slice(0, 20).map((row, i) => (
                              <tr key={i} className="border-b border-border-light dark:border-border-dark last:border-0 hover:bg-background-light dark:hover:bg-background-dark transition-colors">
                                {message.data.dimensions.map((dim) => (
                                  <td key={dim} className="px-4 py-3 text-sm">{row[dim]}</td>
                                ))}
                                {message.data.metrics.map((metric) => (
                                  <td key={metric} className="px-4 py-3 text-sm">
                                    {isDurationMetric(metric)
                                      ? formatDuration(row[metric])
                                      : typeof row[metric] === 'number'
                                        ? row[metric].toLocaleString('cs-CZ')
                                        : row[metric]}
                                  </td>
                                ))}
                              </tr>
                            ))}
                          </tbody>
                        </table>
                        {message.data.rows.length > 20 && (
                          <p className="text-center py-2 text-muted-light dark:text-muted-dark text-sm">
                            Zobrazeno prvních 20 z {message.data.rows.length} záznamů
                          </p>
                        )}
                      </div>
                    </details>
                  )}

                  {/* Follow-up suggestions after assistant message */}
                  {idx === messages.length - 1 && !loading && (
                    <FollowUpSuggestions
                      lastQuery={messages.find(m => m.type === 'user')?.content}
                      lastData={message.data}
                      onSelect={(query) => {
                        setInput(query);
                        document.querySelector('input[type="text"]')?.focus();
                      }}
                    />
                  )}
                </>
              )}

              {/* Error Message */}
              {message.type === 'error' && (
                <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4">
                  <div className="flex items-center gap-2 text-red-700 dark:text-red-300 font-semibold mb-2">
                    <span className="material-symbols-outlined">error</span>
                    {message.error.title}
                  </div>
                  <p className="text-red-600 dark:text-red-400 mb-2">{message.error.message}</p>
                  <p className="text-red-500 dark:text-red-400 text-sm italic mb-3 flex items-center gap-2">
                    <span className="material-symbols-outlined text-sm">lightbulb</span>
                    {message.error.suggestion}
                  </p>

                  <div className="flex gap-2">
                    {message.error.retryable && (
                      <button
                        className="px-4 py-2 bg-primary text-white rounded-lg hover:bg-opacity-90 transition-colors flex items-center gap-2 text-sm font-semibold"
                        onClick={() => handleRetry(message.originalQuery)}
                      >
                        <span className="material-symbols-outlined text-sm">refresh</span>
                        Zkusit znovu
                      </button>
                    )}

                    {message.error.requiresLogin && (
                      <button
                        className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors flex items-center gap-2 text-sm font-semibold"
                        onClick={() => window.location.reload()}
                      >
                        <span className="material-symbols-outlined text-sm">login</span>
                        Přihlásit se znovu
                      </button>
                    )}
                  </div>

                  {/* Show raw data if available even with error */}
                  {message.data?.rows?.length > 0 && (
                    <div className="mt-4">
                      <p className="mb-2 text-muted-light dark:text-muted-dark text-sm flex items-center gap-2">
                        <span className="material-symbols-outlined text-sm">table_chart</span>
                        Surová data (bez AI analýzy):
                      </p>
                      <div className="overflow-x-auto border border-border-light dark:border-border-dark rounded-lg">
                        <table className="w-full">
                          <thead className="bg-primary text-white">
                            <tr>
                              {message.data.dimensions.map((dim) => (
                                <th key={dim} className="px-3 py-2 text-left text-xs font-semibold">{dim}</th>
                              ))}
                              {message.data.metrics.map((metric) => (
                                <th key={metric} className="px-3 py-2 text-left text-xs font-semibold">{metric}</th>
                              ))}
                            </tr>
                          </thead>
                          <tbody className="bg-surface-light dark:bg-surface-dark">
                            {message.data.rows.slice(0, 10).map((row, i) => (
                              <tr key={i} className="border-b border-border-light dark:border-border-dark last:border-0">
                                {message.data.dimensions.map((dim) => (
                                  <td key={dim} className="px-3 py-2 text-xs">{row[dim]}</td>
                                ))}
                                {message.data.metrics.map((metric) => (
                                  <td key={metric} className="px-3 py-2 text-xs">
                                    {isDurationMetric(metric)
                                      ? formatDuration(row[metric])
                                      : typeof row[metric] === 'number'
                                        ? row[metric].toLocaleString('cs-CZ')
                                        : row[metric]}
                                  </td>
                                ))}
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* System Message */}
              {message.type === 'system' && (
                <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-3 text-blue-700 dark:text-blue-300">
                  {message.content}
                </div>
              )}
            </div>
          </div>
        ))}

        {/* Loading Progress */}
        {loading && (
          <div className="flex items-start gap-4">
            <div className="flex-shrink-0 w-8 h-8 rounded-full bg-primary-light dark:bg-primary-dark flex items-center justify-center">
              <span className="material-symbols-outlined text-base text-primary">auto_awesome</span>
            </div>
            <div className="flex-1">
              <ProgressSteps currentStep={progressStep} onCancel={handleCancel} />
            </div>
          </div>
        )}

        <div ref={messagesEndRef} />
      </div>

      {/* Input Area */}
      <div className="flex-shrink-0 pt-4 space-y-3">
        <ComparisonHelper
          onSelect={(query) => {
            setInput(query);
            document.querySelector('input[type="text"]')?.focus();
          }}
        />

        <form onSubmit={handleSubmit} className="flex items-center gap-4">
          <input
            type="text"
            value={input}
            onChange={(e) => setInput(e.target.value)}
            placeholder="Zeptejte se na vaše GA4 data..."
            className="flex-1 px-4 py-3 text-base bg-surface-light dark:bg-surface-dark border border-border-light dark:border-border-dark rounded-lg focus:ring-primary focus:border-primary placeholder-muted-light dark:placeholder-muted-dark shadow-sm"
            disabled={loading}
          />
          <button
            type="submit"
            disabled={loading || !input.trim()}
            className="flex-shrink-0 px-6 py-3 text-base font-semibold text-white bg-primary rounded-lg shadow-sm hover:bg-opacity-90 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary dark:focus:ring-offset-background-dark transition-colors flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Send
            <span className="material-symbols-outlined text-xl">send</span>
          </button>
        </form>
      </div>
    </div>
  );
}
